import React from 'react';
import {
  CardListItem,
  CardListItemProps
} from 'record-it-components/cardList/CardListItem/CardListItem';
import { CardListItemIconButton } from 'record-it-components/cardList/CardListItemIconButton/CardListItemIconButton';
import { CardListItemIconLink } from 'record-it-components/cardList/CardListItemIconLink/CardListItemIconLink';
import { CardListItemTitle } from 'record-it-components/cardList/CardListItemTitle/CardListItemTitle';
import { useModalOpened } from 'record-it-components/hooks/useModalOpened/useModalOpened';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { TimeTrackingActivity } from '../../../classes/entities/TimeTrackingActivity/timeTrackingActivityTypes';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';
import { useTimeTrackingActivityDeleteHandler } from '../../../hooks/entity/deleteHandler/useTimeTrackingActivityDeleteHandler';
import { EditTimeTrackingActivityDialog } from '../EditTimeTrackingActivityDialog/EditTimeTrackingActivityDialog';

export type TimeTrackingActivityListItemProps =
  PropsWithoutChildren<CardListItemProps> & {
    readonly timeTrackingActivity: TimeTrackingActivity;
  };

// eslint-disable-next-line react/no-multi-comp
export const TimeTrackingActivityListItem = React.memo(
  React.forwardRef<HTMLDivElement, TimeTrackingActivityListItemProps>(
    (props, ref) => {
      const { timeTrackingActivity, ...remainingProps } = prepareProps({
        props,
        ignoreChildren: true
      });

      const {
        open: openEditDialog,
        close: closeEditDialog,
        modalProps
      } = useModalOpened({ closeOnBackDropClick: true });

      const handleDeleteClick =
        useTimeTrackingActivityDeleteHandler(timeTrackingActivity);

      return (
        <>
          <CardListItem
            ref={ref}
            {...remainingProps}
          >
            <CardListItemTitle data-testid="TimeTrackingActivityListItem.Name">
              {timeTrackingActivity.name}
            </CardListItemTitle>
            <CardListItemIconButton
              {...iconConfigurations.prefixed.listItem.deleteIcon}
              onClick={handleDeleteClick}
              data-testid="TimeTrackingActivityListItem.DeleteButton"
            ></CardListItemIconButton>
            <CardListItemIconButton
              {...iconConfigurations.prefixed.listItem.editInDialogIcon}
              onClick={openEditDialog}
            ></CardListItemIconButton>
            {/* since we currently only support 2 layers of activities, we just hide this link if we are not at the root level */}
            {!timeTrackingActivity.parentActivityId ? (
              <CardListItemIconLink
                {...iconConfigurations.prefixed.listItem.enterListItem}
                to={RoutePaths.adminTimeTrackingActivities(
                  timeTrackingActivity.compilationId,
                  timeTrackingActivity.id
                )}
                data-testid="TimeTrackingActivityListItem.NavigateToSubEntities"
              ></CardListItemIconLink>
            ) : null}
          </CardListItem>
          <EditTimeTrackingActivityDialog
            {...modalProps}
            timeTrackingActivity={timeTrackingActivity}
            onAccept={closeEditDialog}
          ></EditTimeTrackingActivityDialog>
        </>
      );
    }
  )
);
